import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Box,
  Stack,
  Grid,
  Typography,
  Breadcrumbs,
  Divider,
} from '@mui/material';
import { Button } from '../../../../../ui';
import { TextArea } from '../../../../../ui/input/text_area/text_area';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import { ToggleInput } from '../../../../../ui/input/toggle_input/toggle_input';
import {
  getAssessment,
  getAssessmentSettings,
  updateAssessmentSettings,
  updateAssessmentStatus,
} from '../../../../../utils/api_service/assessment_api';
import { AssessmentContext } from '../../../../../utils/contexts/contexts';
import {
  Assessment,
  AssessmentSettings,
} from '../../../../../utils/types/assessment_types';

export const SettingsAndPermissions: React.FC = () => {
  const history = useHistory();
  const { assessmentState, setAssessmentState } = useContext(AssessmentContext);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [settings, setSettings] = useState<AssessmentSettings>({
    assessmentId: assessmentState.assessmentId,
    showScore: false,
    customMessage: '',
    shuffleQuestions: false,
    enableCopy: false,
    attemptCount: '1',
    blurVideo: false,
    unsuccessfulEmail: false,
    thresholdPercent: '0',
  });

  useEffect(() => {
    getAssessment(assessmentState.assessmentId).then((response: Assessment) => {
      if (response.id === assessmentState.assessmentId) {
        setIsActive(response.isActive);
        getAssessmentSettings(assessmentState.assessmentId).then(
          (response: AssessmentSettings) => {
            if (response.assessmentId === assessmentState.assessmentId) {
              setSettings({
                ...response,
                attemptCount: response.attemptCount.toString(),
                thresholdPercent:
                  response.thresholdPercent &&
                  response.thresholdPercent.toString(),
              });
            }
          },
        );
      }
    });
  }, [assessmentState.assessmentId]);

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSettings({
      ...settings,
      [event.target.name]: event.target.value,
    });
  };

  const navigateBackToQuestions = () => {
    saveAssessment();
    setAssessmentState({
      ...assessmentState,
      assessmentPageNo: 2,
    });
  };

  const saveAssessment = () => {
    updateAssessmentStatus(assessmentState.assessmentId, isActive).then(
      (response) => {
        if (response.ok) {
          updateAssessmentSettings(assessmentState.assessmentId, settings).then(
            (response) => {
              if (response.ok) {
                history.push('./assessment-list');
              }
            },
          );
        }
      },
    );
  };

  return (
    <Stack spacing={3} sx={{ width: '100%', height: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 1.5 }}>
        <Breadcrumbs aria-label='breadcrumb'>
          <Typography
            component={Link}
            to='#'
            variant='body2'
            color='text.secondary'
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Create Assessment
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Permissions and Settings
          </Typography>
        </Breadcrumbs>
      </Box>

      <Grid container spacing={4}>
        {/* Left Column */}
        <Grid item xs={12} md={6}>
          <Stack spacing={3} divider={<Divider />}>
            {/* Custom Message */}
            <Box>
              <TextArea
                label='Custom End-of-Test Message'
                name='customMessage'
                value={settings.customMessage}
                onChange={handleInputChange}
              />
            </Box>

            {/* Attempt Count */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant='subtitle2' fontWeight={600} color='text.primary'>
                  Number of Attempts
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Maximum number of times a candidate can retake the assessment.
                </Typography>
              </Box>
              <Box sx={{ width: 100 }}>
                <TextInput
                  label=''
                  name='attemptCount'
                  value={settings.attemptCount}
                  type='number'
                  onChange={handleInputChange}
                  min='1'
                  max='10'
                />
              </Box>
            </Box>

            {/* Threshold Percentage */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant='subtitle2' fontWeight={600} color='text.primary'>
                  Threshold Percentage
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Minimum score (%) required to pass.
                </Typography>
              </Box>
              <Box sx={{ width: 100 }}>
                <TextInput
                  label=''
                  name='thresholdPercent'
                  value={settings.thresholdPercent || '0'}
                  type='number'
                  onChange={handleInputChange}
                  min='1'
                  max='100'
                />
              </Box>
            </Box>

            {/* Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant='subtitle2' fontWeight={600} color='text.primary'>
                  Status of Test
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Switch to make this assessment active or inactive.
                </Typography>
              </Box>
              <ToggleInput
                toggled={isActive}
                handleToggle={() => setIsActive(!isActive)}
                name='isActive'
                informationText='Switch to make active/inactive'
              />
            </Box>

            {/* Show Score */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant='subtitle2' fontWeight={600} color='text.primary'>
                  Show Score at End
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Display the candidate’s total score when they finish.
                </Typography>
              </Box>
              <ToggleInput
                toggled={settings.showScore}
                handleToggle={() =>
                  setSettings({ ...settings, showScore: !settings.showScore })
                }
                name='showScore'
                informationText='Show total score'
              />
            </Box>
          </Stack>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={6}>
          <Stack spacing={3} divider={<Divider />}>
            {/* Shuffle Questions */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant='subtitle2' fontWeight={600} color='text.primary'>
                  Shuffle Questions
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Randomize the order of questions for each candidate.
                </Typography>
              </Box>
              <ToggleInput
                toggled={settings.shuffleQuestions}
                handleToggle={() =>
                  setSettings({
                    ...settings,
                    shuffleQuestions: !settings.shuffleQuestions,
                  })
                }
                name='shuffleQuestions'
                informationText='Shuffle question order'
              />
            </Box>

            {/* Restrict Cut/Copy/Paste */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant='subtitle2' fontWeight={600} color='text.primary'>
                  Restrict Cut/Copy/Paste
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Prevent candidates from copying or pasting content during the test.
                </Typography>
              </Box>
              <ToggleInput
                toggled={!settings.enableCopy}
                handleToggle={() =>
                  setSettings({ ...settings, enableCopy: !settings.enableCopy })
                }
                name='enableCopy'
                informationText='Restrict cut/copy/paste'
              />
            </Box>

            {/* Blur Candidate Video */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant='subtitle2' fontWeight={600} color='text.primary'>
                  Blur Candidate Video
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Maintain candidate anonymity by blurring their video feed.
                </Typography>
              </Box>
              <ToggleInput
                toggled={settings.blurVideo}
                handleToggle={() =>
                  setSettings({ ...settings, blurVideo: !settings.blurVideo })
                }
                name='blurVideo'
                informationText='Blur face of the candidate'
              />
            </Box>

            {/* Candidate-specific Settings */}
            {assessmentState.assessmentType === 'candidate' && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant='subtitle2' fontWeight={600} color='text.primary'>
                    Automatic Unsuccessful Email
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Send an email when a candidate does not meet the threshold.
                  </Typography>
                </Box>
                <ToggleInput
                  toggled={settings.unsuccessfulEmail || false}
                  handleToggle={() =>
                    setSettings({
                      ...settings,
                      unsuccessfulEmail: !settings.unsuccessfulEmail,
                    })
                  }
                  name='unsuccessfulEmail'
                  informationText='Automatic Unsuccessful Email'
                />
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Stack
        direction='row'
        justifyContent='flex-end'
        spacing={2}
        sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}
      >
        <Button
          onClick={navigateBackToQuestions}
          variant='secondary'
          label='Back'
        />
        <Button onClick={saveAssessment} variant='primary' label='Save' />
      </Stack>
    </Stack>
  );
};
